import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  Heart, Video, Clock, Users, Sparkles, MessageCircle, Star, 
  Play, Pause, Volume2, ChevronRight, Award, Shield, Zap,
  CheckCircle, ArrowRight, Target, Trophy, Smile, Coffee
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import EnhancedButton from './EnhancedButton';
import SEOHead from './SEOHead';

const EnhancedHowItWorks = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [spotsLeft, setSpotsLeft] = useState(50);
  const videoRef = useRef(null);
  const promoVideoRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  // Smart random spots counter - decreases through the week, resets Monday
  useEffect(() => {
    const calculateSpots = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const hour = now.getHours();
      
      // Reset on Monday (day 1), start fresh at 50
      // Gradually decrease through the week
      // Sunday (0) -> treat as day 7
      const effectiveDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      // Base decrease: ~4 spots per day on average
      const dayDecrease = (effectiveDay - 1) * 4;
      
      // Hour-based micro decrease (0-3 spots based on hour)
      const hourDecrease = Math.floor(hour / 8);
      
      // Add some randomness (-2 to +2)
      const randomOffset = Math.floor(Math.random() * 5) - 2;
      
      // Calculate final spots (minimum 12, maximum 50)
      const spots = Math.max(12, Math.min(50, 50 - dayDecrease - hourDecrease + randomOffset));
      
      setSpotsLeft(spots);
    };
    
    calculateSpots();
    
    // Update every 30 minutes for subtle changes
    const interval = setInterval(calculateSpots, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-play promo video on mount
  useEffect(() => {
    if (promoVideoRef.current) {
      promoVideoRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
      });
    }
  }, []);

  const steps = [
    {
      id: 1,
      title: "Create Your Perfect Profile",
      subtitle: "Make a lasting first impression",
      description: "Build a compelling profile that showcases your personality, interests, and what makes you unique. Our AI helps optimize your profile for better matches.",
      icon: Users,
      color: "from-rose-400 to-pink-500",
      features: [
        "AI-powered profile optimization",
        "Photo verification system",
        "Interest matching algorithm",
        "Privacy controls"
      ],
      tips: [
        "Upload 3-5 recent photos",
        "Write a genuine bio",
        "List your real interests",
        "Be authentic and positive"
      ]
    },
    {
      id: 2,
      title: "Join Smart Speed Dating Sessions",
      subtitle: "AI-matched conversations in real-time",
      description: "Get matched with compatible singles for 3-5 minute video conversations. Our AI ensures you're paired with people who share your interests and values.",
      icon: Video,
      color: "from-purple-400 to-indigo-500",
      features: [
        "HD video quality",
        "Smart compatibility matching",
        "Real-time AI moderation",
        "Safe, secure environment"
      ],
      tips: [
        "Find good lighting",
        "Wear something nice",
        "Choose a quiet location",
        "Smile and be yourself"
      ]
    },
    {
      id: 3,
      title: "Break the Ice with AI Suggestions",
      subtitle: "Never run out of things to talk about",
      description: "Our AI assistant provides personalized icebreaker questions and topic suggestions to keep your conversations flowing naturally.",
      icon: Sparkles,
      color: "from-blue-400 to-cyan-500",
      features: [
        "AI-generated icebreakers",
        "Personality-based questions",
        "Real-time topic suggestions",
        "Cultural conversation guides"
      ],
      tips: [
        "Listen actively",
        "Ask follow-up questions",
        "Share personal stories",
        "Show genuine interest"
      ]
    },
    {
      id: 4,
      title: "Make Your Connection Decision",
      subtitle: "Simple yes or no - powered by intuition",
      description: "At the end of each conversation, decide if you felt a spark. Our algorithm learns from your choices to suggest better matches over time.",
      icon: Heart,
      color: "from-green-400 to-emerald-500",
      features: [
        "Instant decision feedback",
        "Match preference learning",
        "Anonymous until mutual interest",
        "No pressure environment"
      ],
      tips: [
        "Trust your instincts",
        "Consider compatibility",
        "Don't overthink it",
        "Be honest with yourself"
      ]
    },
    {
      id: 5,
      title: "Celebrate Your Matches!",
      subtitle: "When sparks fly, connections happen",
      description: "When two people both show interest, it's a match! Continue your conversation privately and see where your connection leads.",
      icon: Trophy,
      color: "from-yellow-400 to-orange-500",
      features: [
        "Instant match notifications",
        "Private chat messaging",
        "Date planning tools",
        "Success rate tracking"
      ],
      tips: [
        "Send a thoughtful message",
        "Suggest meeting in person",
        "Plan a fun first date",
        "Stay positive and open"
      ]
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      age: 28,
      location: "San Francisco",
      text: "I met my boyfriend through Shot Clock Dating! The timed conversations made it so much less awkward than regular dating apps.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Michael R.",
      age: 32,
      location: "New York",
      text: "The AI matching really works. I had meaningful conversations from day one instead of endless swiping.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Emily L.",
      age: 26,
      location: "Austin",
      text: "Speed dating online? I was skeptical, but this is actually so much fun and I've made real connections!",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
    }
  ];

  const stats = [
    { label: "Success Rate", value: "73%", icon: Target },
    { label: "Average Matches", value: "8.5", icon: Heart },
    { label: "Happy Couples", value: "12,847", icon: Smile },
    { label: "Daily Sessions", value: "2,400+", icon: Coffee }
  ];

  const StepIndicator = ({ step, isActive, isCompleted }) => (
    <motion.div
      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold text-lg transition-all duration-300 ${
        isCompleted
          ? 'bg-green-500 border-green-500 text-white'
          : isActive
          ? 'bg-rose-500 border-rose-500 text-white'
          : 'border-gray-300 text-gray-400'
      }`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
    >
      {isCompleted ? <CheckCircle className="w-6 h-6" /> : step}
    </motion.div>
  );

  const AnimatedCounter = ({ value, duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(null);
    const inView = useInView(countRef, { once: true });

    useEffect(() => {
      if (inView) {
        let startTime = null;
        const isPercentage = value.includes('%');
        const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
        
        const animate = (currentTime) => {
          if (startTime === null) startTime = currentTime;
          const progress = Math.min((currentTime - startTime) / duration, 1);
          
          setCount(Math.floor(progress * numericValue));
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        
        requestAnimationFrame(animate);
      }
    }, [inView, value, duration]);

    return (
      <span ref={countRef}>
        {count}{value.includes('%') ? '%' : value.includes('+') ? '+' : value.includes(',') ? ',' : ''}
        {value.includes(',') && count > 999 ? Math.floor(count/1000) + ',' + (count%1000).toString().padStart(3, '0') : ''}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden max-w-full" style={{ overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      <SEOHead 
        title="How It Works - AI-Powered Speed Dating Made Simple | Shot Clock Dating"
        description="Discover how Shot Clock Dating revolutionizes online dating with AI-powered speed dating sessions. Learn our 5-step process for meaningful connections through timed video conversations."
        keywords="how speed dating works, AI dating process, video speed dating guide, online dating tutorial, dating app instructions, speed dating explained, virtual dating sessions"
        url="https://shotclockdating.com/how-it-works"
      />

      {/* Compact Hero Section */}
      <motion.div 
        className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600"
        style={{ y, maxWidth: '100vw', overflowX: 'hidden' }}
      >
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/20"></div>
          {/* Floating Hearts Animation - Reduced */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-white/20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100],
                rotate: [0, 360],
                opacity: [0.3, 0, 0.3],
              }}
              transition={{
                duration: 4 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 4,
              }}
            >
              <Heart className="w-4 h-4" />
            </motion.div>
          ))}
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col items-center justify-center">
          <motion.div 
            className="text-center w-full"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title on Top */}
            <motion.div
              className="mb-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-white px-4">
                How It<span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent"> Works</span>
              </h1>
            </motion.div>

            {/* Limited Spots Counter */}
            <motion.div
              className="mb-3"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 border border-white/20">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400"></span>
                </span>
                <span className="text-white/90 text-sm font-medium">
                  Only <span className="text-yellow-300 font-bold">{spotsLeft}</span> spots left this week
                </span>
              </div>
            </motion.div>

            {/* Promo Video */}
            <motion.div
              className="mb-4 max-w-md mx-auto px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-white/30">
                <video
                  ref={promoVideoRef}
                  className="w-full h-auto"
                  controls
                  loop
                  autoPlay
                  muted
                  playsInline
                  data-testid="how-it-works-video"
                  style={{ maxHeight: '45vh' }}
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_videodating-hub/artifacts/er5dmi8z_Final%20ShotClock_4.mp4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
            
            {/* Description */}
            <motion.p 
              className="text-base text-white/90 mb-4 max-w-lg mx-auto px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Master the art of AI-powered speed dating in five simple steps and start making meaningful connections today!
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 px-4 w-full"
            >
              <EnhancedButton
                onClick={() => navigate('/register')}
                size="default"
                variant="secondary"
                className="bg-white text-rose-500 hover:bg-gray-100 w-full sm:w-auto"
                icon={<Play className="w-5 h-5" />}
              >
                Start Your Journey
              </EnhancedButton>
              
              {/* Google Sign-Up Button */}
              <Button
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  // Redirect to /auth/callback — the dedicated handler route. /profile/setup
                  // is ProtectedRoute-wrapped and would reject the user before session_id exchange.
                  const redirectUrl = window.location.origin + '/auth/callback';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="bg-white hover:bg-gray-100 text-gray-800 py-2.5 px-4 rounded-xl font-semibold transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 shadow-lg w-full sm:w-auto text-sm"
                data-testid="google-signup-how-it-works"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Interactive Progress Bar */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 py-4 overflow-x-auto">
          <div className="flex items-center justify-between min-w-0">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-x-auto scrollbar-hide">
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className="flex items-center space-x-1 sm:space-x-2 group flex-shrink-0"
                >
                  <StepIndicator 
                    step={step.id} 
                    isActive={currentStep === step.id}
                    isCompleted={completedSteps.includes(step.id)}
                  />
                  <span className={`hidden md:block font-medium text-sm ${
                    currentStep === step.id ? 'text-rose-500' : 'text-gray-600'
                  }`}>
                    {step.title}
                  </span>
                </button>
              ))}
            </div>
            <Badge variant="outline" className="text-rose-500 border-rose-200 flex-shrink-0 ml-2">
              Step {currentStep} of {steps.length}
            </Badge>
          </div>
        </div>
      </div>

      {/* Interactive Steps Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 overflow-x-hidden" style={{ maxWidth: '100vw' }}>
        {steps.map((step) => (
          <motion.div
            key={step.id}
            className={`${currentStep === step.id ? 'block' : 'hidden'}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
              <div>
                <motion.div 
                  className="flex items-center mb-6"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`bg-gradient-to-r ${step.color} text-white rounded-full w-16 h-16 flex items-center justify-center mr-6 shadow-lg`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{step.title}</h2>
                    <p className="text-lg text-gray-600">{step.subtitle}</p>
                  </div>
                </motion.div>
                
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  {step.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <Star className="w-5 h-5 text-yellow-500 mr-2" />
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {step.features.map((feature, idx) => (
                        <motion.li 
                          key={idx}
                          className="flex items-center text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <Zap className="w-5 h-5 text-blue-500 mr-2" />
                      Pro Tips
                    </h4>
                    <ul className="space-y-2">
                      {step.tips.map((tip, idx) => (
                        <motion.li 
                          key={idx}
                          className="flex items-center text-gray-600"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 + 0.2 }}
                        >
                          <ArrowRight className="w-4 h-4 text-rose-500 mr-2 flex-shrink-0" />
                          {tip}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <EnhancedButton
                    onClick={() => {
                      if (!completedSteps.includes(step.id)) {
                        setCompletedSteps([...completedSteps, step.id]);
                      }
                      if (step.id < steps.length) {
                        setCurrentStep(step.id + 1);
                      } else {
                        // Navigate to registration page when on final step
                        navigate('/register');
                      }
                    }}
                    variant="primary"
                    icon={<ChevronRight className="w-5 h-5" />}
                  >
                    {step.id === steps.length ? 'Get Started' : 'Next Step'}
                  </EnhancedButton>
                  
                  {step.id > 1 && (
                    <EnhancedButton
                      onClick={() => setCurrentStep(step.id - 1)}
                      variant="outline"
                    >
                      Previous
                    </EnhancedButton>
                  )}
                </div>
              </div>
              
              <motion.div 
                className={`bg-gradient-to-br ${step.color} rounded-2xl p-6 lg:p-8 text-center text-white shadow-2xl overflow-hidden`}
                whileHover={{ scale: 1.02, rotateY: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ 
                  background: `linear-gradient(135deg, var(--tw-gradient-stops))`,
                  transform: 'perspective(1000px)'
                }}
              >
                <step.icon className="w-32 h-32 mx-auto mb-6" />
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-lg opacity-90">{step.subtitle}</p>
                
                {step.id === 2 && (
                  <motion.div 
                    className="mt-6 bg-white/20 rounded-lg p-4"
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
                  >
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <Video className="w-5 h-5" />
                      <span className="font-medium">Demo Session</span>
                    </div>
                    <p className="text-sm opacity-75">Watch a 30-second preview</p>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Success Stats Section */}
      <motion.div 
        className="bg-gradient-to-r from-indigo-600 to-purple-600 py-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h3 
            className="text-3xl font-bold text-white mb-12"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Success Speaks for Itself
          </motion.h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                className="text-center"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  <AnimatedCounter value={stat.value} />
                </div>
                <div className="text-lg text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Testimonials Carousel */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.h3 
          className="text-3xl font-bold text-gray-900 text-center mb-12"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
        >
          Real Success Stories
        </motion.h3>
        
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, idx) => (
            <motion.div
              key={idx}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.2 }}
              whileHover={{ y: -10 }}
            >
              <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.age} • {testimonial.location}</p>
                    </div>
                  </div>
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Final CTA Section */}
      <motion.div 
        className="bg-gradient-to-r from-rose-500 to-pink-500 py-20"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Ready to Find Your Perfect Match?
          </motion.h2>
          
          <motion.p 
            className="text-xl text-white/90 mb-8"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Join thousands of singles who've found meaningful connections through AI-powered speed dating
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <EnhancedButton
              onClick={() => navigate('/register')}
              size="xl"
              variant="secondary"
              className="bg-white text-rose-500 hover:bg-gray-100"
              icon={<Heart className="w-6 h-6" />}
            >
              Start Dating Now
            </EnhancedButton>
            
            <EnhancedButton
              onClick={() => navigate('/advanced-features')}
              size="xl"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              icon={<Sparkles className="w-6 h-6" />}
            >
              Explore Features
            </EnhancedButton>
          </motion.div>
          
          <motion.div 
            className="mt-8 text-white/80"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center justify-center space-x-6">
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                <span>100% Safe & Secure</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Free to Start</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default EnhancedHowItWorks;